
import { getAddressBookDefault, createJobTicket, getJobTicketsByJobIdShort } from '@/api';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { fetchReferencesParams, fetchSetJobOnHold, fetchUnsetJobOnHold } from '@/helpers/fetchers';

import LookupAddresses from '@/components/UI/LookupAddresses.vue';
import LookupCustomerCode from '@/components/UI/LookupCustomerCode.vue';
import LookupProductModel from '@/components/UI/LookupProductModel.vue';
import LookupServices from '@/components/UI/LookupServices.vue';
import LookupDriver from '@/components/UI/LookupDriver.vue';
import LookupProductTypes from '@/components/UI/LookupProductTypes.vue';
import LookupProductPrefix from '@/components/UI/LookupProductPrefix.vue';
import LookupSuburb from '@/components/UI/LookupSuburb.vue';
import LookupWarehouses from '@/components/UI/LookupWarehouses.vue';
import SelectAttributes from '@/components/UI/SelectAttributes.vue';
import DateTimezoneToggle from '@/components/UI/dateTimezoneToggle/DateTimezoneToggle.vue';
import Icon from '@/components/UI/Icon.vue';
import SectionEditPlaceholder from '@/components/UI/SectionEditPlaceholder.vue';
import InputIntegers from '@/components/UI/InputIntegers.vue';
import Button from '@/components/UI/Button.vue';
import ZammadTicketCreationForm from '@/components/jobDetailed/ZammadTicketCreationForm.vue';
import ZammadList from '@/components/jobDetailed/ZammadList.vue';

import { format, utcToZonedTime } from 'date-fns-tz';
import { statesTimeZonesList, timeZoneDefault } from '@/utils/PortalConstants';
import { convertTimeString12to24 } from '@/utils/InputProcessingUtilities';
import { parseSpecialInstructions } from '@/utils/Common';

import { ReferencesParams, UploadingFilesObjectForTable, User } from '@/models/main';
import { AddressBookAirRoadStop, AddressBookDefaultData } from '@/models/addressBook';
import {
  JobComplianceAttribute,
  JobComplianceData,
  JobComplianceDriver,
  JobComplianceHiddenData,
  JobComplianceEditingSections,
  JobComplianceLoaders,
  JobComplianceDriverAllocateParams,
  JobComplianceStopService,
  JobComplianceStop,
} from '@/models/JobComplianceTypes';
import { ModuleStaticDataState } from '@/store/modules/staticData/staticDataTypes';
import store from '@/store';
import { fetchDetailedJob, fetchJobPriceLeg } from '@/helpers/fetchers';

import OpenSchedulerBoardFromJobLink from '@/components/btns/OpenSchedulerBoardFromJobLink.vue';
import SplitToBase from '@/components/jobBooking/JobActions/SplitToBase.vue';
import Add3rdStop from '@/components/jobBooking/JobActions/Add3rdStop.vue';
import Remove3rdStop from '@/components/jobBooking/JobActions/Remove3rdStop.vue';
import AddStairsService from '@/components/jobBooking/JobActions/AddStairsService.vue';
import JobCancel from '@/components/jobBooking/JobActions/JobCancel.vue';
import JobCloseAsFutile from '@/components/jobBooking/JobActions/JobCloseAsFutile.vue';
import JobChangeCustomer from '@/components/jobBooking/JobActions/JobChangeCustomer.vue';
import JobPaymentStatusDisplay from '@/components/jobBooking/JobActions/JobPaymentStatusDisplay.vue';
import JobHoldAction from '@/components/jobBooking/JobActions/JobHoldAction.vue';

import JobAttachmentsField from '@/components/fields/JobAttachmentsField.vue';
import { cloneJob, getModelFromString } from '@/helpers/booking';
import { JobTicket, JobTicketResponce } from '@/models/jobTicket';
import { cleanEmailString, generateUserEmailName, uuidv4 } from '@/utils/Common';
import { notifyNetworkError, notifyNetworkSuccess } from '@/helpers/notifiers';
import * as routeNames from '@/router/routeNames';
import * as Sentry from '@sentry/vue';
import InputPositiveNumbers from '../UI/InputPositiveNumbers.vue';
import { generateEmailSignature } from '@/helpers/zammadEmailTemplates';
import { getBaseName } from '@/utils/Common';
import InvoicedJobWarning from '@/components/UI/InvoicedJobWarning.vue';

@Component({
  components: {
    JobAttachmentsField,
    Button,
    JobHoldAction,
    InputIntegers,
    InputPositiveNumbers,
    DateTimezoneToggle,
    Icon,
    InvoicedJobWarning,
    JobPaymentStatusDisplay,
    LookupAddresses,
    LookupSuburb,
    LookupCustomerCode,
    LookupProductModel,
    LookupServices,
    LookupDriver,
    LookupProductTypes,
    LookupProductPrefix,
    LookupWarehouses,
    OpenSchedulerBoardFromJobLink,
    SectionEditPlaceholder,
    SelectAttributes,
    SplitToBase,
    Add3rdStop,
    Remove3rdStop,
    AddStairsService,
    JobCancel,
    JobCloseAsFutile,
    JobChangeCustomer,
    ZammadTicketCreationForm,
    ZammadList,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class JobComplianceModal extends Vue {
  @Prop({ type: Object, required: false, default: false }) public formData: JobComplianceData;
  @Prop({ type: Array, required: false, default: false }) public addressData: AddressBookAirRoadStop[];
  @Prop({ type: Object, required: false, default: false }) public jobHiddenData: JobComplianceHiddenData;
  @Prop({ type: Object, required: false, default: false }) public editingSections: JobComplianceEditingSections;
  @Prop({ type: Object, required: false, default: false }) public loaders: JobComplianceLoaders;
  @Prop({ type: Object, required: false, default: false }) public jobProcessingState;

  public user: User;
  public routeNames = routeNames;
  public jobEditError = false;
  public loadingRfcRequest = false;
  private files: File[] = [];
  public filesObjectsForTable: UploadingFilesObjectForTable[] = [];
  public uploadError = '';
  public timeZoneDefault = timeZoneDefault;
  public newMailOpen = false;
  public newMailOpenToTop = true;
  public opsEmails: string[] = [];
  private channelsEmailList: string[] = [];
  public format = format;
  public utcToZonedTime = utcToZonedTime;
  public getBaseName = getBaseName;

  public staticListsData: ModuleStaticDataState = {
    attributes: [],
    drivers: [],
    serviceCodes: [],
    productTypes: [],
  };

  public zammadTickets: JobTicketResponce[] = [];
  public isCanceling: boolean;

  public krakenPriceData: JobComplianceStopService[][] = [];
  public populatingPriceDataFromKraken = false;

  public get showSectionDisable() {
    return (
      !!this.jobProcessingState.hasError ||
      !!this.jobProcessingState.cannotBeEdited ||
      !!this.jobProcessingState.hasChanged ||
      !!this.jobProcessingState.isEditingByOther ||
      this.loaders.saving
    );
  }

  public parseSpecialInstructions = parseSpecialInstructions;

  public stopsAttributes = {
    0: [],
    1: [],
    2: [],
  };

  private driverChosen: JobComplianceDriver = {
    id: 0,
    code: 0,
    full_name: '',
    mobile_phone: '',
  };

  // Invoice and Payment Status Properties
  public isHoldButtonLoading = false;
  public isConfirmingHold = false;
  public isConfirmingUnhold = false;

  public get shouldShowHoldButton(): boolean {
    return (
      this.formData.invoice_status.toUpperCase() === 'PENDING' &&
      this.formData.payment_status.toUpperCase() === 'PENDING'
    );
  }

  public get shouldShowUnholdButton(): boolean {
    return (
      this.formData.invoice_status.toUpperCase() === 'ON HOLD' &&
      this.formData.payment_status.toUpperCase() === 'ON HOLD'
    );
  }

  /**
   * Completes the hold/unhold action after confirmation
   */
  public async completeHoldAction(): Promise<void> {
    this.isHoldButtonLoading = true;

    try {
      const jobId = this.formData.rockhopper_id;
      const jobDate = this.formData.rockhopper_date;

      const isOnHold =
        this.formData.invoice_status.toUpperCase() === 'ON HOLD' &&
        this.formData.payment_status.toUpperCase() === 'ON HOLD';

      isOnHold ? await fetchUnsetJobOnHold(jobId, jobDate) : await fetchSetJobOnHold(jobId, jobDate);

      await this.$emit('fetchJobReload');
    } catch {
      void {};
    } finally {
      // Reset loading state and close confirmation dialog
      this.isHoldButtonLoading = false;
      this.isConfirmingHold = false;
      this.isConfirmingUnhold = false;
    }
  }

  public ticketFormData = {
    subject: '',
    msg: '',
    toChips: [],
    ccChips: [],
  };

  public getOpenSectionName() {
    let sectionName = '';
    Object.keys(this.editingSections).some(section => {
      if (this.editingSections[section] === true) {
        sectionName = section;
      }
    });
    return sectionName;
  }

  // Sections editing
  public stopsDateLinkClicked(idx: number): void {
    this.jobHiddenData.dateTimes[idx].dateIsLinked = !this.jobHiddenData.dateTimes[idx].dateIsLinked;
    // When it's `true`, set the second date the same as the first
    if (this.jobHiddenData.dateTimes[idx].dateIsLinked) {
      this.jobHiddenData.dateTimes[idx].dateTo = this.jobHiddenData.dateTimes[idx].dateFrom;
      const prevDateStringTime = this.formData.stops[idx].time_latest.split('T')[1];
      this.formData.stops[idx].time_latest = this.jobHiddenData.dateTimes[idx].dateFrom + 'T' + prevDateStringTime;
    }
  }

  public populateServiceCodesFromKraken(stopIndex: number): void {
    this.populatingPriceDataFromKraken = true;
    this.editSectionClicked(`services${stopIndex}`);
    this.formData.stops[stopIndex].services = [];
    this.krakenPriceData[stopIndex].forEach(service => {
      this.formData.stops[stopIndex].services.push({
        name: service.name,
        quantity: typeof service.quantity === 'string' ? parseInt(service.quantity, 10) : service.quantity,
        second_quantity:
          typeof service.second_quantity === 'string' ? parseInt(service.second_quantity, 10) : service.second_quantity,
        scheduled_fee: typeof service.fee === 'string' ? parseFloat(service.fee) : service.fee,
        fee: typeof service.fee === 'string' ? parseFloat(service.fee) : service.fee,
        gst: typeof service.gst === 'string' ? parseFloat(service.gst) : service.gst,
        pay: typeof service.pay === 'string' ? parseFloat(service.pay) : service.pay,
      });
    });
    this.$nextTick(() => {
      this.populatingPriceDataFromKraken = false;
    });
  }

  public async editSectionClicked(section: string): Promise<void> {
    let form = this.$refs[section];
    if (form && form[0]) {
      form = form[0];
    }
    const openSectionChecker = Object.values(this.editingSections).every(v => v === false);
    if (openSectionChecker) {
      this.$emit('editSectionClicked', section, form);
    } else {
      this.$emit('callAnimatePanel');
    }
  }

  public reallocateDriver(params: JobComplianceDriverAllocateParams): void {
    this.$emit('reallocateDriver', params);
  }

  public editingBgClicked(): void {
    this.$emit('callAnimatePanel');
  }

  public canScheduleClicked(): void {
    this.$emit('canScheduleClicked');
  }

  public async logOutClicked(): Promise<void> {
    await store.dispatch('moduleAuthData/removeAuthData');
    await store.dispatch('moduleUser/removeUser');
    await store.dispatch('setLoginError', 'Error when getting static lists');
    await this.$router.push('/login');
  }

  public downloadImage(file): void {
    window.open(file.file, '_blank');
  }

  // STOP SECTIONS
  public addressChanged(address: AddressBookAirRoadStop, stop_number: number): void {
    if (typeof address === 'string') {
      this.formData.stops[stop_number].name = address;
    } else {
      this.formData.stops[stop_number].address_line_1 = address.line1;
      this.formData.stops[stop_number].address_line_2 = address.line2;
      this.formData.stops[stop_number].is_warehouse = address.warehouse;
      if (address.suburb) {
        this.formData.stops[stop_number].suburb = address.suburb.name;
        this.formData.stops[stop_number].post_code = address.suburb.postcode;
        this.formData.stops[stop_number].state = address.suburb.state;
        const lookupSuburb = this.$refs[`lookupSuburb${stop_number}`] as HTMLFormElement[];
        lookupSuburb[0].applyValueFromParent({
          name: address.suburb.name,
          id: address.suburb.id,
          postcode: address.suburb.postcode,
          state: address.suburb.state,
        });
      }
    }
    this.$forceUpdate();
  }

  public suburbLookupChanged(value, idx): void {
    this.formData.stops[idx].state = value.state;
    this.formData.stops[idx].post_code = value.postcode;
    this.formData.stops[idx].suburb = value.name;
  }

  public stopDateChanged(value: string, idx: number, name: string): void {
    const fieldName = name === 'from' ? 'time_earliest' : 'time_latest';
    if (this.formData.stops[idx][fieldName]) {
      const prevDateStringTime = this.formData.stops[idx][fieldName].split('T')[1];
      this.formData.stops[idx][fieldName] = value + 'T' + prevDateStringTime;
      if (this.jobHiddenData.dateTimes[idx].dateIsLinked) {
        const linkedFielddName = name === 'from' ? 'time_latest' : 'time_earliest';
        const linkedHiddenFielddName = name === 'from' ? 'dateTo' : 'dateFrom';
        const prevLinkedDateStringTime = this.formData.stops[idx][linkedFielddName].split('T')[1];
        this.formData.stops[idx][linkedFielddName] = value + 'T' + prevLinkedDateStringTime;
        this.jobHiddenData.dateTimes[idx][linkedHiddenFielddName] = value;
      }
    }
  }

  public stopTimeChanged(value: string, idx: number, name: string): void {
    const fieldName = name === 'from' ? 'time_earliest' : 'time_latest';
    if (this.formData.stops[idx][fieldName]) {
      name === 'from'
        ? (this.jobHiddenData.dateTimes[idx].timeFromInitial = value)
        : (this.jobHiddenData.dateTimes[idx].timeToInitial = value);
      const oldDateValue = this.formData.stops[idx][fieldName].split('T')[0];
      const newTimeValue = convertTimeString12to24(value) + ':00';
      const stopTimeZone = statesTimeZonesList[this.formData.stops[idx].state].timezone;
      const zoneString = format(utcToZonedTime(new Date(), stopTimeZone), 'xxx', {
        timeZone: stopTimeZone,
      });
      const newDateTimeValue = oldDateValue + 'T' + newTimeValue + zoneString;
      this.formData.stops[idx][fieldName] = newDateTimeValue;
    }
  }

  public durationChanged(value: string, idx: number): void {
    this.formData.stops[idx].duration = Number(value);
  }

  public addStopAttributes(value: JobComplianceAttribute, idx: number): void {
    this.stopsAttributes[idx].push(value);
    this.formData.stops[idx].attributes.push(value.code);
  }

  public removeStopAttributes(value: JobComplianceAttribute, idx: number): void {
    const attrIdx = this.stopsAttributes[idx].findIndex(x => x.description === value.description);
    this.stopsAttributes[idx].splice(attrIdx, 1);
    const attrDataIdx = this.formData.stops[idx].attributes.indexOf(value.code);
    this.formData.stops[idx].attributes.splice(attrDataIdx, 1);
  }

  public calculateDriverButtonsState(): void {
    if (this.formData.allocation_state === 'allocated') {
      this.jobHiddenData.driverButtonsState = {
        deallocate: 'off',
        preallocate: 'disable',
        allocate: 'on',
      };
    } else if (this.formData.allocation_state === 'preallocated') {
      this.jobHiddenData.driverButtonsState = {
        deallocate: 'off',
        preallocate: 'on',
        allocate: 'off',
      };
    } else {
      this.jobHiddenData.driverButtonsState = {
        deallocate: 'on',
        preallocate: 'off',
        allocate: 'off',
      };
    }
  }

  private applyStopsData(stops?): void {
    const stopsToApply = stops ? stops : this.formData.stops;
    stopsToApply.map((stop, i) => {
      // Addresses
      const lookupAddress = this.$refs[`lookupAddress${i}`] as HTMLFormElement[];
      if (lookupAddress && lookupAddress[0]) {
        lookupAddress[0].applyValueFromParent({
          city: null,
          line1: stop.address_line_1,
          line2: stop.address_line_2,
          line3: null,
          name: stop.name,
          suburb: {
            id: i,
            name: stop.suburb,
            postcode: stop.post_code,
            state: stop.state,
          },
          id: 0,
          warehouse: stop.is_warehouse,
        });
      }
      // Suburbs
      const lookupSuburb = this.$refs[`lookupSuburb${i}`] as HTMLFormElement[];
      lookupSuburb[0].applyValueFromParent({
        name: stop.suburb,
        id: i, // not real??
        postcode: stop.post_code,
        state: stop.state,
      });

      // Date & times
      const stopTimeZone = statesTimeZonesList[stop.state]?.timezone || timeZoneDefault;

      this.jobHiddenData.dateTimes[i].dateFrom = format(
        utcToZonedTime(new Date(stop.time_earliest), stopTimeZone),
        'yyyy-MM-dd',
      );
      this.jobHiddenData.dateTimes[i].dateTo = format(
        utcToZonedTime(new Date(stop.time_latest), stopTimeZone),
        'yyyy-MM-dd',
      );
      this.jobHiddenData.dateTimes[i].timeFromInitial = format(
        utcToZonedTime(new Date(stop.time_earliest), stopTimeZone),
        'hh:mm aa',
      );
      this.jobHiddenData.dateTimes[i].timeToInitial = format(
        utcToZonedTime(new Date(stop.time_latest), stopTimeZone),
        'hh:mm aa',
      );

      this.jobHiddenData.dateTimes[i].dateIsLinked =
        this.jobHiddenData.dateTimes[i].dateFrom === this.jobHiddenData.dateTimes[i].dateTo;

      // Attributes
      this.stopsAttributes[i] = [];
      stop.attributes.map(attr => {
        const chosenAttr = this.staticListsData.attributes.find(x => x.code === attr);
        if (chosenAttr) {
          this.stopsAttributes[i].push(chosenAttr);
        }
      });
      this.applyProductsData(i);
    });

    // Get a driver to render in lookup
    // To make this we need to check if state is not deallocated
    // If so then find a first driver from the end which is not 0
    let currentDriver: number | null = null;
    if (this.formData.allocation_state !== 'deallocated') {
      const drivers: boolean[] = stopsToApply.map(stop => !!stop.driver_is);
      currentDriver = stopsToApply[drivers.lastIndexOf(true)].driver_is;
    } else {
      currentDriver = null;
    }
    // Set buttons
    this.calculateDriverButtonsState();

    this.jobHiddenData.driverStatus = this.formData.allocation_state;
    this.jobHiddenData.driverStatusChosen = null;
    const lookupDriver = this.$refs['driverLookup'] as HTMLFormElement;
    const chosenDriver = this.staticListsData.drivers.find(x => x.code === currentDriver);

    if ((!!currentDriver || currentDriver === 0) && chosenDriver) {
      this.jobHiddenData.driverCode = currentDriver;
      lookupDriver.applyValueFromParent(chosenDriver);
    }
  }

  get isDriverSelectDisabled(): boolean {
    const { driverStatus, driverStatusChosen } = this.jobHiddenData;

    // Case 1: Driver is deallocated and no status chosen
    if (driverStatus === 'deallocated' && driverStatusChosen === null) {
      return true;
    }

    // Case 2: Explicit deallocation chosen
    if (driverStatusChosen === 'deallocate') {
      return true;
    }

    // Case 3: Driver is allocated and matches the form driver
    if (
      driverStatus === 'allocated' &&
      this.driverChosen.code > 0 &&
      this.driverChosen.code === this.formData.driver_code
    ) {
      return true;
    }

    return false;
  }

  public async defaultAddressIconClicked(stop_number: number): Promise<void> {
    await getAddressBookDefault(this.formData.customer)
      .then((res: AddressBookDefaultData) => {
        const lookupAddress = this.$refs[`lookupAddress${stop_number}`] as HTMLFormElement[];
        if (lookupAddress[0]) {
          lookupAddress[0].applyValueFromParent({ ...res });
        }
        this.formData.stops[stop_number].note_text = res.note_text;
        this.formData.stops[stop_number].note_name = res.note_name;
        this.formData.stops[stop_number].note_phone = res.note_phone;
        this.formData.stops[stop_number].note_email = res.note_email || '';
      })
      .catch(() => {
        notifyNetworkError({ title: 'Error getting the default address' });
      });
  }

  // DRIVER / ALLOCATIONS
  public driverChanged(value: JobComplianceDriver): void {
    this.driverChosen = value;
    this.jobHiddenData.driverCode = value.code;
  }

  public driverStatusClicked(action: 'deallocate' | 'preallocate' | 'allocate'): void {
    // ??? Unknown IF
    // if (this.jobHiddenData.driverStatus !== 'allocated' || (!!this.formData.split_base && action === 'deallocate')) {
    //   this.jobHiddenData.driverStatusChosen = action;
    // }
    switch (action) {
      case 'deallocate':
        this.jobHiddenData.driverStatusChosen = action;
        if (this.jobHiddenData.driverStatus === 'allocated') {
          this.jobHiddenData.driverButtonsState = {
            deallocate: 'on',
            preallocate: 'disable',
            allocate: 'off',
          };
        } else {
          this.jobHiddenData.driverButtonsState = {
            deallocate: 'on',
            preallocate: 'off',
            allocate: 'off',
          };
        }
        break;
      case 'preallocate':
        if (this.jobHiddenData.driverStatus === 'allocated') {
          break;
        }
        this.jobHiddenData.driverStatusChosen = action;
        this.jobHiddenData.driverButtonsState = {
          deallocate: 'off',
          preallocate: 'on',
          allocate: 'off',
        };
        break;
      case 'allocate':
        this.jobHiddenData.driverStatusChosen = action;
        this.jobHiddenData.driverButtonsState = {
          deallocate: 'off',
          preallocate: 'disable',
          allocate: 'on',
        };
        break;
    }
  }

  // Used in JobCompliance.vue
  public applyDriverData(): void {
    this.calculateDriverButtonsState();
    const lookupDriver = this.$refs['driverLookup'] as HTMLFormElement;
    lookupDriver.applyValueFromParent({
      code: this.jobHiddenData.driverCode,
    });
    const driverForm = this.$refs['driver'] as HTMLFormElement;
    driverForm.reset();
  }

  public async handleCloneJobClicked(): Promise<void> {
    const job = await fetchDetailedJob(this.formData.rockhopper_id, this.formData.rockhopper_date);
    cloneJob(job, this.$router);
  }

  // PRODUCTS
  public productPrefixChanged(value, stop_number, product_number) {
    const prevVal = Object.values(this.formData.stops[stop_number].pickup_products)[product_number].model;
    const prefixList = ['OUT: RECYCLE: ', 'OUT: STORE: ', 'OUT: RETURN: ', 'OUT: WORKSHOP: ', 'OUT: ', 'IN: '];
    const prefix = new RegExp('^(' + prefixList.join('|') + ')', 'g');
    const afterLastPrefix = prevVal.replace(prefix, '');
    if (value === '-') {
      Object.values(this.formData.stops[stop_number].pickup_products)[product_number].model = afterLastPrefix;
    } else {
      Object.values(this.formData.stops[stop_number].pickup_products)[product_number].model =
        value + ': ' + afterLastPrefix;
    }
  }

  public productModelChanged(value, stop_number, product_number) {
    this.formData.stops[stop_number].pickup_products[
      Object.keys(this.formData.stops[stop_number].pickup_products)[product_number]
    ].model = value.name;
    this.formData.stops[stop_number].pickup_products[
      Object.keys(this.formData.stops[stop_number].pickup_products)[product_number]
    ].model_id = value.product_id;

    this.productsChanged(value, stop_number, product_number);
  }

  public productsChanged(value, idx: number, jdx: number): void {
    Object.values(this.formData.stops[idx].pickup_products)[jdx].type_code = value.type_code;
    Object.values(this.formData.stops[idx].pickup_products)[jdx].weight = value.weight;
    Object.values(this.formData.stops[idx].pickup_products)[jdx].width = value.width;
    Object.values(this.formData.stops[idx].pickup_products)[jdx].height = value.height;
    Object.values(this.formData.stops[idx].pickup_products)[jdx].length = value.length;
  }

  public inputIntegersProductChanged(value, field, stop_number, product_number) {
    Object.values(this.formData.stops[stop_number].pickup_products)[product_number][field] = Number(value);
  }

  public removeProductClicked(idx: number, jdx: number): void {
    const newProducts = { ...this.formData.stops[idx].pickup_products };
    const id = Object.keys(this.formData.stops[idx].pickup_products)[jdx];
    delete newProducts[id];

    this.formData.stops[idx].pickup_products = { ...newProducts };
  }

  public addProductClicked(idx: number): void {
    this.formData.stops[idx].pickup_products = {
      ...this.formData.stops[idx].pickup_products,
      [uuidv4()]: {
        id: null,
        model: '',
        model_id: null,
        description: '',
        serial: '',
        delivery_stop_number: idx + 2,
        weight: 1,
        pieces: 1,
        type_code: '',
        primary_service: '',
        quant_id: null,
        volume: '',
        width: 0,
        height: 0,
        length: 0,
      },
    };
    // Reset validation after adding a product
    const productsRef = this.$refs[`products${idx}`] as HTMLFormElement[];
    if (productsRef?.[0]) {
      productsRef[0].reset();
    }
  }

  public calculateActualDuration(stop_number: number): number | null {
    const stop = this.formData.stops[stop_number];
    if (!stop.arrived_at || !stop.departed_at) {
      return null;
    }
    const arrivedAt = new Date(stop.arrived_at);
    const departedAt = new Date(stop.departed_at);
    return Math.round((departedAt.getTime() - arrivedAt.getTime()) / 60000);
  }

  private applyProductsData(stop_number: number): void {
    Object.values(this.formData.stops[stop_number].pickup_products).map((product, jdx) => {
      const inputModel = this.$refs[`lookupProducts-${stop_number}-${jdx}`] as HTMLFormElement[];
      inputModel[0]?.applyValueFromParent(getModelFromString(product.model));
      // Input Quantity
      const inputQuantity = this.$refs[`inputQuantity-${stop_number}-${jdx}`] as HTMLFormElement[];
      inputQuantity[0]?.applyValueFromParent(String(product.pieces));
    });
  }

  // SERVICE CODES
  public serviceCodeChanged(value, stop_number, product_number) {
    this.formData.stops[stop_number].services[product_number].name = value;
  }

  public serviceCodeQuantityChanged(value: string | number, stop_number: number, service_number: number): void {
    this.formData.stops[stop_number].services[service_number].quantity = Number(value);
    this.formData.stops[stop_number].services[service_number].second_quantity = Number(value);
  }

  public serviceCodeSecondQuantityChanged(value: string | number, stop_number: number, service_number: number): void {
    this.formData.stops[stop_number].services[service_number].second_quantity = Number(value);
  }

  public serviceCodeFeeChanged(value: string | number, stop_number: number, service_number: number): void {
    this.formData.stops[stop_number].services[service_number].fee = Number(value);
    if (!this.populatingPriceDataFromKraken) {
      this.recalculateServiceFeeChanged(stop_number, service_number);
    }
  }

  public serviceCodeScheduledFeeChanged(value: string | number, stop_number: number, service_number: number): void {
    this.formData.stops[stop_number].services[service_number].scheduled_fee = Number(value);
  }

  public serviceCodeGSTChanged(value: string | number, stop_number: number, service_number: number): void {
    this.formData.stops[stop_number].services[service_number].gst = Number(value);
    if (!this.populatingPriceDataFromKraken) {
      this.recalculateServiceGstChanged(stop_number, service_number);
    }
  }

  public serviceCodePayChanged(value: string | number, stop_number: number, service_number: number): void {
    this.formData.stops[stop_number].services[service_number].pay = Number(value);
  }

  public calcStopTotal(services: JobComplianceStopService[], name: string): number {
    return Number(services.reduce((acc, service) => acc + Number(service[name]), 0).toFixed(2));
  }

  public calcJobTotal(stops: JobComplianceStop[], name: string): number {
    return Number(stops.reduce((acc, stop) => acc + this.calcStopTotal(stop.services, name), 0).toFixed(2));
  }

  public recalculateServiceFeeChanged(stop_number: number, service_number: number): void {
    const service = this.formData.stops[stop_number].services[service_number];
    service.gst = Number((service.fee * 0.1).toFixed(2));
    const feeAfterGST = service.fee - service.gst;
    service.pay = Number((feeAfterGST * 0.5).toFixed(2));
  }

  public recalculateServiceGstChanged(stop_number: number, service_number: number): void {
    const service = this.formData.stops[stop_number].services[service_number];
    const feeAfterGST = service.fee - service.gst;
    service.pay = Number((feeAfterGST * 0.5).toFixed(2));
  }

  public addServiceCodeClicked(idx: number): void {
    this.formData.stops[idx].services.push({
      name: '',
      quantity: 1,
      second_quantity: 0,
      scheduled_fee: 0,
      fee: 0,
      gst: 0,
      pay: 0,
    });
    // Reset validation after adding a service
    const servicesRef = this.$refs[`services${idx}`] as HTMLFormElement[];
    if (servicesRef?.[0]) {
      servicesRef[0].reset();
    }
  }

  public removeServiceCodeClicked(idx: number, jdx: number): void {
    this.formData.stops[idx].services.splice(jdx, 1);
  }

  private applyServicesData(stop_number: number): void {
    this.formData.stops[stop_number].services.map((service, jdx) => {
      // Products model lookup
      const lookupService = this.$refs[`lookupService-${stop_number}-${jdx}`] as HTMLFormElement[];
      if (lookupService && lookupService[0]) {
        lookupService[0].applyValueFromParent({
          code: service.name,
          description: '',
        });
      }
      // Input Quantity
      const inputServiceQuantity = this.$refs[`inputServiceQuantity-${stop_number}-${jdx}`] as HTMLFormElement[];
      if (inputServiceQuantity && inputServiceQuantity[0]) {
        inputServiceQuantity[0].applyValueFromParent(String(service.quantity));
      }
    });
  }

  // MAIL SECTION
  public newMailClicked(): void {
    this.newMailOpen = !this.newMailOpen;
  }

  public newMailClickedOutside(): void {
    this.newMailOpen = false;
  }

  public zammadArticleClicked(article): void {
    const routeData = this.$router.resolve({ path: `/job/ticket/${article.id}` });
    window.open(routeData.href, '_blank');
  }

  public async ticketFormSubmitted(formData: {
    msg: string;
    subject: string;
    is_rfc: boolean;
    toChips: string[];
    ccChips: string[];
    attachments: File[];
  }) {
    this.loadingRfcRequest = true;
    const params: ReferencesParams = await fetchReferencesParams(this.formData.customer);
    const companyName = params?.name || this.user.customer;
    const group = process.env.VUE_APP_SENTRY_ENVIRONMENT === 'production' ? 'Service Delivery Team' : 'IT Support';

    const bodyRfc = `
    <div>
      <h3>A new request for change of the Job ${companyName},
      #<a href='https://portal.kkts.com.au/job/${this.formData.rockhopper_id}'>${this.formData.rockhopper_id}</a>,
      ref: ${this.formData.reference_1}</h3><br/>
      <div>${this.ticketFormData.msg}</div>
    </div>`;

    const bodyNote = `
    <div>
      <h3>Job ${companyName},
      #<a href='https://portal.kkts.com.au/job/${this.formData.rockhopper_id}'>${this.formData.rockhopper_id}</a>,
      ref: ${this.formData.reference_1}</h3><br/>
      <div>${this.ticketFormData.msg}</div>
    </div>`;

    let body = formData.is_rfc ? bodyRfc : bodyNote;

    if (this.$ability.can('staff', 'Job')) {
      const signature = generateEmailSignature({
        fullName: generateUserEmailName(this.user),
        email: this.user.email || 'info@kkts.com.au',
        phone: this.user.phone_number || '(02) 9575-0000',
        group: group,
      });
      body += '<br/><br/>' + signature;
    }

    const ticketData: JobTicket = {
      title: formData.subject,
      group: group,
      customer: store.getters['moduleUser/user'].email.toLowerCase(),
      article: {
        subject: formData.is_rfc ? `[RFC] ${this.ticketFormData.subject}` : this.ticketFormData.subject,
        body: body,
        type: this.$ability.can('staff', 'Job') && !formData.is_rfc ? 'email' : 'note',
        content_type: 'text/html',
        sender: this.$ability.can('staff', 'Job') ? 'Agent' : 'Customer',
        internal: false,
        attachments: formData.attachments,
        ...(!formData.is_rfc && this.$ability.can('staff', 'Job')
          ? { to: cleanEmailString(this.ticketFormData.toChips, this.channelsEmailList).join(', ') }
          : {}),
        ...(!formData.is_rfc && this.$ability.can('staff', 'Job')
          ? { cc: cleanEmailString(this.ticketFormData.ccChips, this.channelsEmailList).join(', ') }
          : {}),
      },
      rh_job: this.formData.rockhopper_id,
      is_rfc: formData.is_rfc,
    };

    createJobTicket(ticketData)
      .then(res => {
        this.newMailOpen = false;
        this.zammadTickets.push(res);
        notifyNetworkSuccess({ text: 'The ticket was successfully created' });
        this.$emit('fetchJobReload', true, true);
      })
      .catch(() => {
        notifyNetworkError({ title: 'An error occurred when creating the ticket' });
      })
      .finally(() => {
        this.loadingRfcRequest = false;
      });
  }

  // ATTACHMENTS SECTION
  public deleteFiles(id) {
    const idx = this.jobHiddenData.filesToRemove.indexOf(id);
    if (idx >= 0) {
      this.jobHiddenData.filesToRemove.splice(idx, 1);
    } else {
      this.jobHiddenData.filesToRemove.push(id);
    }
  }

  public handleFetchJobCancel() {
    this.isCanceling = true;
    this.$emit('fetchJobReload', this.formData.rockhopper_id, this.formData.rockhopper_date, false);
  }

  private async getKrakenPriceData(legIndex?: number) {
    const fetchLegData = async (leg: number) => {
      const data = await fetchJobPriceLeg(this.formData.rockhopper_id, this.formData.rockhopper_date, leg);
      this.$set(this.krakenPriceData, leg - 1, data);
    };

    // If a specific leg index is provided, update only that leg
    if (legIndex !== undefined) {
      await fetchLegData(legIndex + 1); // +1 because API legs are indexed starting from 1
      return;
    }

    // Otherwise update all legs
    const promises = [fetchLegData(1)];
    if (this.formData.stops.length > 2) {
      promises.push(fetchLegData(2));
    }

    await Promise.all(promises);
  }

  private async mounted() {
    // Static lists:
    // Check static lists in the state
    const allStaticCheckData = await store.getters['moduleStaticData/getAllStaticData'];
    // Request static lists if some is empty
    for (let i = 0; i < Object.keys(allStaticCheckData).length; i++) {
      if (!allStaticCheckData[Object.keys(allStaticCheckData)[i]].length) {
        await store.dispatch('moduleStaticData/requestAllStaticDataWait');
        break;
      }
    }
    // Check again
    this.staticListsData = await store.getters['moduleStaticData/getAllStaticData'];
    // Set edit error if some list is empty
    for (let i = 0; i < Object.keys(this.staticListsData).length; i++) {
      if (!this.staticListsData[Object.keys(this.staticListsData)[i]].length) {
        this.jobEditError = true;
        break;
      }
    }

    this.formData.attachments.map(file => {
      const fileName = file.file.substring(file.file.lastIndexOf('/') + 1);
      this.filesObjectsForTable.push({
        name: fileName,
        size: 0,
      });
    });

    getJobTicketsByJobIdShort(this.formData.rockhopper_id, this.formData.rockhopper_date)
      .then(data => {
        this.zammadTickets = data;
      })
      .catch(err => {
        notifyNetworkError({ title: 'Unable to get tickets for this job' });
        Sentry.withScope(scope => {
          scope.setLevel('warning');
          Sentry.captureException(err);
        });
      });

    void this.getKrakenPriceData();

    // Form fill
    this.applyStopsData();
  }
}
